<template>
  <v-container fluid class="pa-0">
    <v-card flat class="mx-auto" max-width="1400">
      <v-card-title class="d-flex justify-space-between align-center py-4 px-6 bg-grey-lighten-4">
        <div>
          <div class="text-h6 font-weight-bold">Lease Statement - {{ leaseReference }}</div>
          <div class="text-body-1 grey--text text--darken-2">
            {{ tenantName }} • {{ propertyAddress }}
          </div>
        </div>
        <div class="d-flex align-center gap-3">
          <v-btn small outlined color="primary" @click="openPaymentAllocation">
            <v-icon small left>mdi-plus</v-icon> Add Payment
          </v-btn>
          <v-btn small outlined color="primary" @click="showAddChargeDialog = true">
            <v-icon small left>mdi-plus</v-icon> Add Charge
          </v-btn>
          <v-btn small outlined color="primary" @click="openAddCreditDialog">
            <v-icon small left>mdi-currency-usd</v-icon> Add Credit
          </v-btn>
          <v-btn small outlined color="primary" @click="showImportExcelDialog = true">
            <v-icon small left>mdi-microsoft-excel</v-icon> Import Excel
          </v-btn>
         
          <PDFExportComponent :report-data="formattedReportData" report-title="LEASE STATEMENT"
            :property-name="pdfPropertyTitle" :start-date="startDate" :end-date="endDate" :logo-base64="base64log"
            :visible-columns="visibleColumns" :format-currency="formatCurrency" :format-date="formatDate"
            :column-definitions="columnDefinitions" />
        </div>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-data-table :headers="transactionHeaders" :items="statementEntries" class="elevation-1" dense
          hide-default-footer disable-pagination>

          <template v-slot:item.datePaid="{ item }">
            {{ formatDate(item.datePaid) }}
          </template>
          <template v-slot:item.debit="{ item }">
            <span v-if="parseFloat(item.debit) > 0" class="error--text font-weight-bold">R {{ item.debit }}</span>
          </template>
          <template v-slot:item.credit="{ item }">
            <span v-if="parseFloat(item.credit) > 0" class="success--text font-weight-bold">
              R {{ item.credit }}

              <v-tooltip bottom v-if="item.type === 'payment' && item.allocationStatus">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" small :color="getAllocationColor(item.allocationStatus)"
                    class="ml-2">
                    {{ getAllocationIcon(item.allocationStatus) }}
                  </v-icon>
                </template>
                <span>{{ getAllocationTooltip(item.allocationStatus) }}</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.type === 'payment'">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on" small color="grey darken-2" class="ml-2"
                    @click="openAllocationInfo(item)">
                    mdi-information-outline
                  </v-icon>
                </template>
                <span>View allocation summary</span>
              </v-tooltip>
            </span>
          </template>
          <template v-slot:item.runningBalance="{ item }">
            <strong :class="parseFloat(item.runningBalance) < 0 ? 'success--text' : 'error--text'">
              R {{ Math.abs(parseFloat(item.runningBalance)).toFixed(2) }}
              <span v-if="parseFloat(item.runningBalance) < 0">(Credit)</span>
            </strong>
          </template>
          <template v-slot:item.actions="{ item }">
            <span v-if="!item.isOpening">
              <v-icon small class="mr-2" @click="editTransaction(item)" color="primary">mdi-pencil</v-icon>
              <v-icon small @click="openDeleteDialog(item)" color="error">mdi-delete</v-icon>
            </span>
          </template>
        </v-data-table>
      </v-card-text>

      <v-dialog v-model="showAddPaymentDialog" max-width="1200" persistent>
        <v-card>
          <v-card-title class="headline teal darken-1 white--text">
            <v-icon left color="white">mdi-bank-transfer-in</v-icon>
            {{ editingPaymentId ? 'Edit' : 'Add' }} Payment – {{ leaseReference }}
          </v-card-title>
          <v-card-text class="pt-6">
            <v-row class="px-6">
              <v-col cols="12" sm="4">
                <v-text-field v-model.number="paymentAlloc.amount" label="Payment Amount Received" prefix="R"
                  type="number" step="0.01" outlined dense placeholder="0.00" autofocus
                  @input="onPaymentAmountChange" />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="paymentAlloc.date" label="Payment Date" type="date" outlined dense
                  @input="onPaymentDateChange" />
                <div v-if="isFuturePaymentDate" class="text-caption grey--text text--darken-1 mt-1 d-flex align-center">
                  <v-icon small color="blue" class="mr-1">mdi-calendar-clock</v-icon>
                  Future payment recorded (post-dated or advance)
                </div>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field :value="paymentAlloc.reference" label="Payment Reference" outlined dense readonly
                  hint="Automatically generated" persistent-hint />
              </v-col>
            </v-row>

            <div class="px-6 mt-4">
              <v-alert dense outlined :color="balanceBeforePayment >= 0 ? 'error' : 'success'" class="mb-4">
                <strong>
                  Balance before this payment:
                  <span class="text-h6 font-weight-bold ml-2">
                    R {{ Math.abs(balanceBeforePayment).toFixed(2) }}
                  </span>
                  <span v-if="balanceBeforePayment >= 0" class="error--text"> (Outstanding)</span>
                  <span v-else class="success--text"> (Credit)</span>
                </strong>
              </v-alert>
            </div>

            <div v-if="isEditingExistingPaymentWithAllocations" class="px-6 mb-4">
              <v-alert dense type="info" outlined class="mb-4">
                <strong>Editing payment with existing allocations</strong><br>
                You can now modify, remove, or add new allocations.
              </v-alert>
            </div>

            <div class="d-flex justify-space-between align-center mb-4 px-6">
              <h3>
                {{ isEditingExistingPaymentWithAllocations ? 'Current Allocations' : 'Available Charges' }}
                ({{ paymentAlloc.allocations.length }} items)
              </h3>
              <div>
                <v-btn small color="primary" @click="smartAllocate" class="mr-2" :disabled="!paymentAlloc.amount">
                  <v-icon left small>mdi-refresh</v-icon> Smart Allocate
                </v-btn>
                <v-btn small color="primary" @click="showAddChargeInAllocation = true">
                  <v-icon left small>mdi-plus</v-icon> Add New Charge
                </v-btn>
              </div>
            </div>

            <v-simple-table dense class="mx-6">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description (editable)</th>
                  <th class="text-right">Outstanding</th>
                  <th class="text-right">Allocate</th>
                  <th width="60"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(alloc, i) in paymentAlloc.allocations" :key="alloc.chargeId || i"
                  :class="{ 'grey lighten-3': alloc.remaining <= 0.01 && alloc.amount <= 0.01 }">
                  <td>{{ formatDate(alloc.date) }}</td>
                  <td>
                    <v-text-field v-model="alloc.description" dense hide-details outlined class="mt-1"
                      style="width:340px" :disabled="alloc.remaining <= 0.01 && alloc.amount <= 0.01" />
                  </td>
                  <td class="text-right font-weight-bold">
                    R {{ alloc.remaining.toFixed(2) }}
                    <v-icon v-if="alloc.remaining <= 0.01" color="success" small class="ml-2">mdi-check-circle</v-icon>
                  </td>
                  <td>
                    <div class="d-flex align-center">
                      <v-text-field v-model.number="alloc.amount" type="number" step="0.01" dense hide-details outlined
                        prefix="R" class="mt-1" style="width:140px" @input="onAllocationInput(i)"
                        :disabled="alloc.remaining <= 0.01 && alloc.previouslyAllocated <= 0.01" />
                      <span v-if="alloc.previouslyAllocated > 0.01" class="ml-3 grey--text caption">
                        (R{{ alloc.previouslyAllocated.toFixed(2) }} prev.)
                      </span>
                      <v-icon v-if="alloc.amount > 0 && alloc.remaining - alloc.amount <= 0.01" color="success"
                        class="ml-3">mdi-check-bold</v-icon>
                    </div>
                    <div v-if="alloc.previouslyAllocated > 0.01" class="text-caption grey--text mt-1">
                      Previously allocated from this payment
                    </div>
                  </td>
                  <td class="text-center">
                    <v-btn icon small color="error" @click="removeAllocation(i)"
                      :disabled="alloc.previouslyAllocated > 0.01 && alloc.amount >= alloc.previouslyAllocated">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <v-expand-transition>
              <div v-if="showAddChargeInAllocation" class="pa-4 mx-6 mt-4 grey lighten-4 rounded">
                <v-row>
                  <v-col cols="12" sm="4">
                    <v-select v-model="newChargeInAllocation.chargeType" :items="chargeTypeOptions" item-text="text"
                      item-value="value" label="Charge Type *" outlined dense></v-select>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field v-model.number="newChargeInAllocation.amount" label="Amount *" prefix="R"
                      type="number" step="0.01" outlined dense></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field v-model="newChargeInAllocation.date" label="Date *" type="date" outlined
                      dense></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-text-field v-model="newChargeInAllocation.description" label="Description (optional)" outlined
                      dense></v-text-field>
                  </v-col>
                </v-row>
                <div class="d-flex justify-end mt-2">
                  <v-btn text @click="showAddChargeInAllocation = false">Cancel</v-btn>
                  <v-btn color="primary" @click="addChargeInAllocation">Add Charge</v-btn>
                </div>
              </div>
            </v-expand-transition>

            <div class="mt-6 pa-4 mx-6 rounded"
              :class="finalBalanceAfterPayment >= 0 ? 'green lighten-5' : 'red lighten-5'">
              <div class="d-flex justify-space-between"><strong>Payment Received:</strong> <strong>R {{
                (paymentAlloc.amount
                  || 0).toFixed(2) }}</strong></div>
              <div class="d-flex justify-space-between"><strong>Allocated to charges:</strong> <strong>R {{
                totalAllocated.toFixed(2) }}</strong></div>
              <div class="d-flex justify-space-between mt-2">
                <strong>Unallocated:</strong>
                <strong
                  :class="unallocatedAmount < -0.01 ? 'error--text font-weight-bold' : (unallocatedAmount > 0.01 ? 'grey--text' : 'success--text')">
                  R {{ Math.abs(unallocatedAmount).toFixed(2) }}
                  <span v-if="unallocatedAmount < -0.01">(Over-allocated!)</span>
                  <span v-else-if="unallocatedAmount > 0.01">(Available)</span>
                  <span v-else>(Fully allocated)</span>
                </strong>
              </div>
              <div class="d-flex justify-space-between mt-4">
                <strong>Balance after this payment:</strong>
                <strong :class="finalBalanceAfterPayment >= 0 ? 'success--text' : 'error--text'">
                  R {{ Math.abs(finalBalanceAfterPayment).toFixed(2) }}
                  <span v-if="finalBalanceAfterPayment > 0.01" class="success--text font-weight-bold"> Credit</span>
                  <span v-else-if="finalBalanceAfterPayment < -0.01" class="error--text font-weight-bold"> Owing</span>
                  <span v-else class="success--text font-weight-bold"> Zero</span>
                </strong>
              </div>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn text @click="cancelPaymentDialog">Cancel</v-btn>
            <v-btn color="teal" dark @click="savePaymentAllocation"
              :disabled="!paymentAlloc.amount || !paymentAlloc.date || savingPayment || unallocatedAmount < -0.01"
              :loading="savingPayment">
              {{ editingPaymentId ? 'Update' : 'Save' }} Payment
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showAddChargeDialog" max-width="600">
        <v-card>
          <v-card-title class="headline">Add Charge</v-card-title>
          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="12" sm="6">
                <v-select v-model="addChargeForm.chargeType" :items="chargeTypeOptions" item-text="text"
                  item-value="value" label="Charge Type *" outlined dense></v-select>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model.number="addChargeForm.amount" label="Amount *" prefix="R" type="number"
                  step="0.01" outlined dense @input="generateChargeReference"></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field v-model="addChargeForm.date" label="Due Date *" type="date" outlined dense
                  @input="generateChargeReference"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field :value="addChargeForm.reference" label="Reference" outlined dense readonly
                  hint="Automatically generated" persistent-hint />
              </v-col>
            </v-row>
            <v-text-field v-model="addChargeForm.description" label="Description (optional – overrides charge type)"
              outlined dense></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showAddChargeDialog = false">Cancel</v-btn>
            <v-btn color="primary" @click="submitAddCharge">Add Charge</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ADD CREDIT DIALOG -->
      <v-dialog v-model="showAddCreditDialog" max-width="600">
        <v-card>
          <v-card-title class="headline">Add Credit / Adjustment</v-card-title>
          <v-card-text class="pt-4">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field v-model="addCreditForm.description" label="Description *" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model.number="addCreditForm.amount" label="Amount (positive) *" prefix="R" type="number"
                  step="0.01" outlined dense @input="generateCreditReference"></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field v-model="addCreditForm.date" label="Date *" type="date" outlined dense
                  @input="generateCreditReference"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field :value="addCreditForm.reference" label="Reference" outlined dense readonly
                  hint="Auto-generated" persistent-hint />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showAddCreditDialog = false">Cancel</v-btn>
            <v-btn color="primary" @click="submitAddCredit">Add Credit</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showEditDialog" max-width="500">
        <v-card>
          <v-card-title>Edit Transaction</v-card-title>
          <v-card-text>
            <v-text-field v-model.number="editForm.amount" label="Amount" type="number" step="0.01"
              prefix="R"></v-text-field>
            <v-text-field v-model="editForm.datePaid" label="Date" type="date"></v-text-field>
            <v-text-field v-model="editForm.description" label="Description"></v-text-field>
            <v-text-field v-model="editForm.reference" label="Reference"></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showEditDialog = false">Cancel</v-btn>
            <v-btn color="primary" @click="submitEdit">Update</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="deleteDialog" max-width="400">
        <v-card>
          <v-card-title class="text-h6 font-weight-bold error--text">
            <v-icon left color="error">mdi-alert</v-icon>
            Confirm Delete
          </v-card-title>
          <v-card-text class="text-body-1">
            Are you sure you want to permanently delete this transaction?<br><br>
            <strong>{{ deleteItem?.description || 'Transaction' }}</strong><br>
            Date: {{ deleteItem ? formatDate(deleteItem.datePaid) : '' }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteDialog = false">Cancel</v-btn>
            <v-btn color="error" dark @click="confirmDelete" :loading="deleting">
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showImportExcelDialog" max-width="1200" persistent>
        <v-card>
          <v-card-title class="headline">Import Lease Statement ({{ leaseReference }})</v-card-title>
          <v-card-text>
            <v-stepper v-model="importStep">
              <v-stepper-header>
                <v-stepper-step :complete="importStep > 1" step="1">Upload</v-stepper-step>
                <v-stepper-step :complete="importStep > 2" step="2">Preview</v-stepper-step>
                <v-stepper-step step="3">Recurring Charges</v-stepper-step>
              </v-stepper-header>
              <v-stepper-items>
                <v-stepper-content step="1">
                  <v-file-input v-model="excelFile" label="Select Excel File" accept=".xlsx"
                    prepend-icon="mdi-microsoft-excel"></v-file-input>
                  <v-btn color="primary" @click="parseExcel" :disabled="!excelFile" :loading="parsingExcel">Parse &
                    Validate</v-btn>
                </v-stepper-content>
                <v-stepper-content step="2">
                  <div v-if="parsedStatement">
                    <v-alert :type="leaseMatches ? 'success' : 'error'" dense class="mb-4">
                      <strong>Excel Lease No: {{ parsedStatement.fileLeaseRef || 'NOT FOUND' }} → Current Lease: {{
                        leaseReference }} → {{ leaseMatches ? 'MATCHED' : 'MISMATCH' }}</strong>
                    </v-alert>
                    <v-alert type="warning" dense v-if="leaseMatches">All current transactions will be
                      replaced.</v-alert>
                    <v-alert type="info" dense>Amount Due: R {{ parsedStatement.amountDue.toFixed(2) }}</v-alert>
                    <h3 class="mt-4">Transactions ({{ parsedStatement.importTransactions.length }})</h3>
                    <div style="max-height:400px;overflow-y:auto;">
                      <v-simple-table dense>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Description</th>
                            <th class="text-right">Debit</th>
                            <th class="text-right">Credit</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(t, i) in parsedStatement.importTransactions" :key="i">
                            <td>{{ formatDate(t.date) }}</td>
                            <td><strong>{{ t.description }}</strong></td>
                            <td class="text-right error--text">{{ t.debit > 0 ? 'R ' + t.debit.toFixed(2) : '' }}</td>
                            <td class="text-right success--text">{{ t.credit > 0 ? 'R ' + t.credit.toFixed(2) : '' }}</td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </div>
                    <v-btn color="primary" large class="mt-4" @click="importStep = 3" :disabled="!leaseMatches">Next →
                      Recurring Charges</v-btn>
                  </div>
                </v-stepper-content>
                <v-stepper-content step="3">
                  <v-alert type="info" dense class="mb-4">Detected recurring charges – select which to
                    create/update:</v-alert>
                  <v-simple-table v-if="detectedRecurring.length">
                    <thead>
                      <tr>
                        <th><v-checkbox v-model="selectAllRecurring"></v-checkbox></th>
                        <th>Type</th>
                        <th>Description</th>
                        <th class="text-right">Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in detectedRecurring" :key="item.chargeType">
                        <td><v-checkbox v-model="item.selected"></v-checkbox></td>
                        <td><strong>{{ item.chargeType }}</strong></td>
                        <td>{{ item.description }}</td>
                        <td class="text-right font-weight-bold">R {{ item.amount.toFixed(2) }}</td>
                        <td><v-chip small :color="item.exists ? 'orange' : 'green'" dark>{{ item.exists ? 'Update' : 'Create'
                            }}</v-chip></td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <div v-else class="text-center pa-8 grey--text text-h6">No recurring charges detected</div>
                  <div class="mt-6">
                    <v-btn color="error" large @click="submitImportTransactions" :loading="importing">Final Import ({{
                      selectedRecurringCount }} recurring)</v-btn>
                    <v-btn text @click="importStep = 2" class="ml-3">Back</v-btn>
                  </div>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="cancelImport">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ALLOCATION INFO DIALOG (end-user view — no refs or IDs) -->
      <v-dialog v-model="showAllocationInfoDialog" max-width="700">
        <v-card v-if="allocationInfo">
          <v-card-title class="headline">
            <v-icon left color="primary">mdi-information-outline</v-icon>
            Payment Allocation Summary
          </v-card-title>
          <v-card-text>
            <v-simple-table dense class="mb-4">
              <tbody>
                <tr>
                  <th style="width: 220px;">Payment Description</th>
                  <td>{{ allocationInfo.payment.description }}</td>
                </tr>
                <tr>
                  <th>Payment Date</th>
                  <td>{{ formatDate(allocationInfo.payment.datePaid) }}</td>
                </tr>
                <tr>
                  <th>Payment Amount</th>
                  <td class="font-weight-bold">R {{ allocationInfo.payment.amount.toFixed(2) }}</td>
                </tr>
              </tbody>
            </v-simple-table>

            <v-alert :type="allocationInfo.alertType" dense outlined class="mb-4">
              <div class="font-weight-bold">{{ allocationInfo.headline }}</div>
              <div class="mt-1">{{ allocationInfo.explanation }}</div>
            </v-alert>

            <div class="font-weight-bold mb-2">
              Charges this payment was applied to ({{ allocationInfo.matchedCharges.length }}):
            </div>

            <v-simple-table v-if="allocationInfo.matchedCharges.length" dense>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th class="text-right">Amount Applied</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(c, i) in allocationInfo.matchedCharges" :key="i">
                  <td>{{ formatDate(c.date) }}</td>
                  <td>{{ c.description }}</td>
                  <td class="success--text font-weight-bold text-right">
                    R {{ c.allocated.toFixed(2) }}
                  </td>
                </tr>
                <tr>
                  <td colspan="2" class="text-right font-weight-bold">Total Applied</td>
                  <td class="text-right font-weight-bold">
                    R {{ allocationInfo.matchedTotal.toFixed(2) }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <v-alert v-else type="warning" dense outlined>
              No charges have this payment applied to them yet.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showAllocationInfoDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-container>
</template>

<script>

import { collection, getDocs, query, where, orderBy, doc, writeBatch, getDoc, setDoc, deleteDoc, updateDoc } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import * as XLSX from 'xlsx';
import PDFExportComponent from '@/views/PDFExportComponent.vue';

const db = getFirestore();

const RECURRING_CHARGE_MAPPING = {
  "Rent": { chargeType: "Rent", description: "Monthly Rent" },
  "Refuse": { chargeType: "Refuse", description: "Refuse" },
  "Insurance": { chargeType: "Insurance", description: "Insurance" },
  "Admin Fee": { chargeType: "Admin Fee", description: "Admin Fee" },
  "Bank Fees": { chargeType: "Bank Fees", description: "Bank Fees" },
  "Bookkeeping": { chargeType: "Bookkeeping", description: "Bookkeeping" },
  "Collection Fee": { chargeType: "Collection Fee", description: "Collection Fee" },
  "Deposit Charged": { chargeType: "Deposit Charged", description: "Deposit Charged" },
  "Fee Raised": { chargeType: "Admin Fee", description: "Admin Fee" },
  "Landscaping": { chargeType: "Landscaping", description: "Landscaping" },
  "Lease Fees": { chargeType: "Lease Fees", description: "Lease Fees" },
  "Legal Expenses": { chargeType: "Legal Expenses", description: "Legal Expenses" },
  "Repair and Maintenance (to Landlord)": { chargeType: "Repair and Maintenance (to Landlord)", description: "Repair and Maintenance (to Landlord)" },
  "Repairs and Maintenance": { chargeType: "Repairs and Maintenance", description: "Repairs and Maintenance" },
  "Sewerage Av": { chargeType: "Sewerage Av", description: "Sewerage Av" },
  "Sewerage Charged": { chargeType: "Sewerage Charged", description: "Sewerage Charged" },
  "Utilities (to Landlord)": { chargeType: "Utilities (to Landlord)", description: "Utilities (to Landlord)" },
  "Utilities-sewerage": { chargeType: "Utilities-sewerage", description: "Utilities - Sewerage" },
  "Utilities": { chargeType: "Utilities", description: "Utilities" },
  "Water Av": { chargeType: "Water Av", description: "Water Av" },
  "Water Charged": { chargeType: "Water Charged", description: "Water Charged" },
  "Other Vendor Expenses": { chargeType: "Other Vendor Expenses", description: "Other Vendor Expenses" },
  "Other Charge": { chargeType: "Other Charge", description: "Other Charge" },
  "Rent due": { chargeType: "Rent", description: "Monthly Rent" },
  "Monthly Rent": { chargeType: "Rent", description: "Monthly Rent" },
  "Rental": { chargeType: "Rent", description: "Monthly Rent" },
  "Refuse recovery": { chargeType: "Refuse", description: "Refuse" },
  "Utilities (To landlord)": { chargeType: "Utilities (to Landlord)", description: "Utilities (to Landlord)" },
  "Repair and Maintenance": { chargeType: "Repair and Maintenance (to Landlord)", description: "Repair and Maintenance (to Landlord)" },
  "Vendor Charges": { chargeType: "Other Vendor Expenses", description: "Other Vendor Expenses" }
};

export default {
  components: { PDFExportComponent },
  props: { leaseReference: { type: String, required: true } },
  data() {
    return {
      base64log: "",
      tenantName: "Loading...",
      propertyAddress: "Loading...",
      transactions: [],
      showAddPaymentDialog: false,
      showAddChargeDialog: false,
      showAddCreditDialog: false,
      showEditDialog: false,
      showImportExcelDialog: false,
      showAddChargeInAllocation: false,
      showAllocationInfoDialog: false,
      allocationInfo: null,
      savingPayment: false,
      editingPaymentId: null,
      existingAllocations: [],
      importStep: 1,
      excelFile: null,
      parsingExcel: false,
      importing: false,
      deleteDialog: false,
      deleteItem: null,
      deleting: false,
      parsedStatement: null,
      detectedRecurring: [],
      paymentAlloc: { amount: null, date: "", reference: "", allocations: [] },
      addChargeForm: {
        chargeType: "Rent",
        amount: null,
        date: new Date().toISOString().substr(0, 10),
        description: "",
        reference: ""
      },
      addCreditForm: {
        description: "",
        amount: null,
        date: new Date().toISOString().substr(0, 10),
        reference: ""
      },
      newChargeInAllocation: {
        chargeType: "Rent",
        amount: null,
        date: new Date().toISOString().substr(0, 10),
        description: ""
      },
      editForm: { id: "", amount: "", datePaid: "", description: "", reference: "" },
      chargeTypeOptions: [
        { text: "Rent", value: "Rent" },
        { text: "Water", value: "Water" },
        { text: "Sewerage", value: "Sewerage" },
        { text: "Refuse", value: "Refuse" },
        { text: "Admin Fee", value: "Admin Fee" },
        { text: "Parking", value: "Parking" },
        { text: "Bank Fees", value: "Bank Fees" },
        { text: "Lease Fees", value: "Lease Fees" },
        { text: "Bookkeeping", value: "Bookkeeping" },
        { text: "Collection Fee", value: "Collection Fee" },
        { text: "Insurance", value: "Insurance" },
        { text: "Landscaping", value: "Landscaping" },
        { text: "Legal Expenses", value: "Legal Expenses" },
        { text: "Repairs and Maintenance", value: "Repairs and Maintenance" },
        { text: "Utilities", value: "Utilities" },
        { text: "Other Vendor Expenses", value: "Other Vendor Expenses" },
        { text: "Other Charge", value: "Other Charge" }
      ],
      transactionHeaders: [
        { text: "Date", value: "datePaid" },
        { text: "Description", value: "description" },
        { text: "Debit", value: "debit", align: "end" },
        { text: "Credit", value: "credit", align: "end" },
        { text: "Balance", value: "runningBalance", align: "end" },
        { text: "Actions", value: "actions", sortable: false, align: "center" }
      ]
    };
  },

  computed: {
    paymentAllocationStatus() {
      const statusMap = new Map();

      const allocationStrings = new Set();
      this.transactions
        .filter(t => t.type === 'charge' || Number(t.amountPaid) > 0)
        .forEach(charge => {
          (charge.allocations || []).forEach(alloc => {
            Object.values(alloc).forEach(v => {
              if (typeof v === 'string' && v.trim().length > 3) {
                allocationStrings.add(v.trim());
              }
            });
          });
        });

      const allocList = Array.from(allocationStrings);
      const normalize = s => (typeof s === 'string' ? s.replace(/\.+$/, '').trim() : s);

      this.transactions
        .filter(t => t.type === 'payment' || (Number(t.amountPaid) < 0 && t.type !== 'credit'))
        .forEach(payment => {
          const identities = [];
          ['id', 'reference', 'paymentRef', 'paymentId'].forEach(k => {
            const v = payment[k];
            if (typeof v === 'string' && v.trim().length > 3) {
              identities.push(normalize(v));
            }
          });
          if (!identities.length) return;

          const hasMatch = allocList.some(rawAllocStr => {
            const allocStr = normalize(rawAllocStr);
            return identities.some(id =>
              allocStr === id ||
              allocStr.includes(id) ||
              id.includes(allocStr)
            );
          });

          if (hasMatch) {
            statusMap.set(payment.id, 'full');
          }
        });

      return statusMap;
    },

    currentBalance() {
      const last = this.statementEntries[this.statementEntries.length - 1];
      return last ? parseFloat(last.runningBalance || 0) : 0;
    },

    balanceBeforePayment() {
      if (!this.paymentAlloc.date) return this.currentBalance;
      const targetDateStr = this.paymentAlloc.date;

      const relevant = this.transactions
        .filter(t => {
          return (t.datePaid < targetDateStr || (t.datePaid === targetDateStr && t.id !== this.editingPaymentId));
        })
        .sort((a, b) => {
          const dateCompare = a.datePaid.localeCompare(b.datePaid);
          if (dateCompare !== 0) return dateCompare;
          return (b.amountPaid || 0) - (a.amountPaid || 0);
        });

      let balance = 0;
      relevant.forEach(t => balance += Number(t.amountPaid || 0));
      return Number(balance.toFixed(2));
    },

    totalAllocated() {
      return Number(this.paymentAlloc.allocations.reduce((s, a) => s + Number(a.amount || 0), 0).toFixed(2));
    },

    unallocatedAmount() {
      const diff = (this.paymentAlloc.amount || 0) - this.totalAllocated;
      return Number(diff.toFixed(2));
    },

    finalBalanceAfterPayment() {
      return Number((this.balanceBeforePayment - (this.paymentAlloc.amount || 0) + this.totalAllocated).toFixed(2));
    },

    allUnpaidCharges() {
      return this.transactions
        .filter(t => Number(t.amountPaid) > 0 && (Number(t.amountPaid) - (Number(t.allocatedAmount) || 0)) > 0.01)
        .map(t => ({
          id: t.id,
          date: t.datePaid,
          description: t.description || t.chargeType || "Charge",
          remaining: Number((t.amountPaid - (t.allocatedAmount || 0)).toFixed(2))
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
    },

    statementEntries() {
      const todayStr = new Date().toISOString().split('T')[0];

      const visible = this.transactions
        .filter(t => t.type !== "payment_allocation" && t.datePaid <= todayStr)
        .sort((a, b) => {
          const dateCompare = a.datePaid.localeCompare(b.datePaid);
          if (dateCompare !== 0) return dateCompare;
          return (b.amountPaid || 0) - (a.amountPaid || 0);
        });

      let balance = 0;
      const entries = visible.map(t => {
        const amt = Number(t.amountPaid) || 0;
        balance += amt;
        return {
          id: t.id,
          datePaid: t.datePaid,
          description: t.description || (amt > 0 ? "Charge" : "Payment"),
          reference: t.reference || "",
          debit: amt > 0 ? amt.toFixed(2) : "0.00",
          credit: amt < 0 ? Math.abs(amt).toFixed(2) : "0.00",
          runningBalance: balance.toFixed(2),
          type: t.type,
          isOpening: false,
          allocationStatus: t.type === 'payment' ? this.paymentAllocationStatus.get(t.id) : null
        };
      });

      if (entries.length > 0) {
        entries.unshift({
          isOpening: true,
          datePaid: entries[0].datePaid,
          description: "Opening Balance",
          reference: "B/F",
          debit: "0.00",
          credit: "0.00",
          runningBalance: "0.00"
        });
      }
      return entries;
    },

    formattedReportData() {
      return this.statementEntries.filter(e => !e.isOpening).map(e => ({
        datePaid: e.datePaid,
        description: e.description,
        debit: parseFloat(e.debit),
        credit: parseFloat(e.credit),
        runningBalance: parseFloat(e.runningBalance)
      }));
    },

    pdfPropertyTitle() { return `${this.propertyAddress} • ${this.tenantName}`; },
    startDate() { return this.formattedReportData[0]?.datePaid || ''; },
    endDate() { return this.formattedReportData[this.formattedReportData.length - 1]?.datePaid || ''; },
    visibleColumns() { return { datePaid: true, description: true, debit: true, credit: true, runningBalance: true }; },
    columnDefinitions() {
      return [
        { field: 'datePaid', header: 'Date', formatter: this.formatDate },
        { field: 'description', header: 'Description' },
        { field: 'debit', header: 'Debit', formatter: v => v > 0 ? this.formatCurrency(v) : '' },
        { field: 'credit', header: 'Credit', formatter: v => v > 0 ? this.formatCurrency(v) : '' },
        { field: 'runningBalance', header: 'Balance', formatter: this.formatCurrency }
      ];
    },

    leaseMatches() { return this.parsedStatement?.fileLeaseRef === this.leaseReference; },
    selectAllRecurring: {
      get() { return this.detectedRecurring.length > 0 && this.detectedRecurring.every(i => i.selected); },
      set(v) { this.detectedRecurring.forEach(i => i.selected = v); }
    },
    selectedRecurringCount() { return this.detectedRecurring.filter(i => i.selected).length; },
    isEditingExistingPaymentWithAllocations() {
      return this.editingPaymentId && this.existingAllocations.length > 0;
    },
    isFuturePaymentDate() {
      if (!this.paymentAlloc.date) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const payDate = new Date(this.paymentAlloc.date);
      return payDate > today;
    }
  },

  methods: {
    debugAllTransactions() {
      const rows = this.transactions
        .sort((a, b) => String(a.datePaid || '').localeCompare(String(b.datePaid || '')))
        .map(t => {
          const amount = Number(t.amountPaid) || 0;
          const allocs = Array.isArray(t.allocations) ? t.allocations : null;
          const allocSummary = allocs
            ? allocs.map(a => (a.paymentRef || a.paymentId || a.ref || '?') + '=' + a.amount).join('; ')
            : 'NO-ARRAY';

          return {
            date: t.datePaid,
            desc: (t.description || '').substring(0, 24),
            amount: amount.toFixed(2),
            type: t.type || '?',
            allocAmt: t.allocatedAmount === undefined ? 'MISSING' : t.allocatedAmount,
            allocCnt: allocs ? allocs.length : 'NO-ARRAY',
            allocs: allocSummary.substring(0, 60),
            refTail: (t.reference || '').substring(Math.max(0, (t.reference || '').length - 10))
          };
        });

      console.log('%c=== ALL TRANSACTIONS (compact) ===', 'color:#1976d2;font-weight:bold;font-size:14px');
      console.log('Lease:', this.leaseReference, '| Total:', this.transactions.length);
      console.table(rows);

      const problems = [];
      this.transactions.forEach(t => {
        const amount = Number(t.amountPaid) || 0;
        const issue = [];
        if (!t.type) issue.push('no type');
        if (amount > 0) {
          if (!Array.isArray(t.allocations)) issue.push('allocations missing');
          if (t.allocatedAmount === undefined) issue.push('allocatedAmount missing');
          if (!t.reference) issue.push('no ref');
          else if (t.reference === this.leaseReference) issue.push('bare ref');
        }
        if (issue.length) {
          problems.push({
            date: t.datePaid,
            desc: (t.description || '').substring(0, 20),
            amount: amount.toFixed(2),
            id: t.id.substring(0, 8) + '…',
            issues: issue.join(', ')
          });
        }
      });

      if (problems.length) {
        console.log(`%c--- PROBLEMS: ${problems.length} ---`, 'color:#c62828;font-weight:bold');
        console.table(problems);
      } else {
        console.log('%c✅ No problems detected', 'color:#2e7d32;font-weight:bold');
      }
    },

    getAllocationIcon(status) {
      if (status === 'full') return 'mdi-check-circle';
      if (status === 'over') return 'mdi-alert-circle';
      return '';
    },

    getAllocationColor(status) {
      if (status === 'full') return 'success';
      if (status === 'over') return 'error';
      return '';
    },

    getAllocationTooltip(status) {
      if (status === 'full') return 'Allocated';
      if (status === 'over') return 'Over-allocated';
      return '';
    },

    // End-user view: no refs, no IDs, no raw dump.
    openAllocationInfo(statementEntry) {
      const raw = this.transactions.find(t => t.id === statementEntry.id) || statementEntry;

      const paymentAmount = Math.abs(Number(raw.amountPaid) || 0);
      const paymentRef = raw.reference || statementEntry.reference || '';
      const paymentId = raw.id || statementEntry.id || '';

      const matchedCharges = [];
      let matchedByRef = 0;
      let matchedById = 0;

      const normalize = (s) => (typeof s === 'string' ? s.replace(/\.+$/, '').trim() : s);
      const payRefNorm = normalize(paymentRef);
      const payIdNorm = normalize(paymentId);

      this.transactions
        .filter(t => t.type === 'charge' || Number(t.amountPaid) > 0)
        .forEach(charge => {
          (charge.allocations || []).forEach(alloc => {
            const allocRef = alloc.paymentRef || '';
            const allocId = alloc.paymentId || alloc.ref || '';
            const amt = Number(alloc.amount) || 0;
            const allocRefNorm = normalize(allocRef);
            const allocIdNorm = normalize(allocId);

            let fieldUsed = null;
            if (payRefNorm && allocRefNorm === payRefNorm) { matchedByRef += amt; fieldUsed = 'paymentRef'; }
            else if (payIdNorm && allocIdNorm === payIdNorm) { matchedById += amt; fieldUsed = 'paymentId/ref'; }
            else if (payIdNorm && allocRefNorm === payIdNorm) { matchedById += amt; fieldUsed = 'paymentRef=payment.id'; }
            else if (payRefNorm && allocRefNorm &&
                     (allocRefNorm.includes(payRefNorm) || payRefNorm.includes(allocRefNorm))) {
              matchedByRef += amt; fieldUsed = 'paymentRef (includes)';
            }

            if (fieldUsed) {
              matchedCharges.push({
                date: charge.datePaid,
                description: charge.description || charge.chargeType || '(no desc)',
                amount: Number(charge.amountPaid) || 0,
                allocated: amt
              });
            }
          });
        });

      const matchedTotal = matchedByRef + matchedById;
      const diff = matchedTotal - paymentAmount;

      let headline = '';
      let explanation = '';
      let alertType = 'info';

      if (matchedTotal === 0) {
        alertType = 'error';
        headline = 'No allocations found for this payment.';
        explanation = 'This payment has not been applied to any charges yet. Open this payment ' +
          'and use Smart Allocate, or allocate manually.';
      } else if (Math.abs(diff) < 0.02) {
        alertType = 'success';
        headline = 'Fully allocated.';
        explanation = 'This payment has been fully applied against outstanding charges.';
      } else if (diff > 0.01) {
        alertType = 'warning';
        headline = 'Over-allocated.';
        explanation = `More has been applied to charges (R ${matchedTotal.toFixed(2)}) than was ` +
          `received (R ${paymentAmount.toFixed(2)}). Edit this payment to correct the allocations.`;
      } else {
        alertType = 'warning';
        headline = 'Partially allocated.';
        explanation = `R ${matchedTotal.toFixed(2)} of R ${paymentAmount.toFixed(2)} has been applied. ` +
          `R ${Math.abs(diff).toFixed(2)} is still unallocated.`;
      }

      this.allocationInfo = {
        payment: {
          description: raw.description || statementEntry.description || 'Payment Received',
          datePaid: raw.datePaid || statementEntry.datePaid,
          amount: paymentAmount
        },
        matchedTotal,
        matchedCharges,
        headline,
        explanation,
        alertType
      };
      this.showAllocationInfoDialog = true;
    },

    formatDate(d) { return d ? new Date(d).toLocaleDateString('en-ZA') : ''; },

    formatCurrency(v) { return `R ${Math.abs(parseFloat(v) || 0).toFixed(2)}`; },

    isFutureDate(dateStr) {
      if (!dateStr) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const d = new Date(dateStr);
      return d > today;
    },

    generatePaymentReference() {
      if (!this.paymentAlloc.date || this.paymentAlloc.amount == null || this.paymentAlloc.amount <= 0) {
        this.paymentAlloc.reference = "";
        return;
      }
      const cleanDate = this.paymentAlloc.date.replace(/-/g, '');
      const cleanAmount = Number(this.paymentAlloc.amount).toFixed(2);
      const ts = Math.floor(Date.now() / 1000);
      this.paymentAlloc.reference = `${this.leaseReference}.${cleanDate}.${cleanAmount}.${ts}`;
    },

    onPaymentAmountChange() {
      this.generatePaymentReference();
      this.clampTotalAllocation();
    },

    onPaymentDateChange() {
      this.generatePaymentReference();
    },

    generateChargeReference() {
      if (!this.addChargeForm.date || this.addChargeForm.amount == null || this.addChargeForm.amount <= 0 || !this.addChargeForm.chargeType) {
        this.addChargeForm.reference = "";
        return;
      }
      const cleanDate = this.addChargeForm.date.replace(/-/g, '');
      const cleanAmount = Number(this.addChargeForm.amount).toFixed(2);
      const shortType = this.addChargeForm.chargeType.substring(0, 4).toUpperCase();
      const ts = Math.floor(Date.now() / 1000);
      this.addChargeForm.reference = `${this.leaseReference}.${shortType}.${cleanDate}.${cleanAmount}.${ts}`;
    },

    onAllocationInput(i) {
      this.clampAllocation(i);
      this.clampTotalAllocation();
    },

    clampAllocation(i) {
      const a = this.paymentAlloc.allocations[i];
      const max = a.remaining + (a.previouslyAllocated || 0);
      if (a.amount > max) a.amount = Number(max.toFixed(2));
      if (a.amount < 0) a.amount = 0;
      a.amount = Number(a.amount.toFixed(2));
    },

    clampTotalAllocation() {
      const max = this.paymentAlloc.amount || 0;
      let total = this.totalAllocated;
      if (total <= max + 0.01) return;
      const sorted = [...this.paymentAlloc.allocations].sort((a, b) => b.date.localeCompare(a.date));
      for (const alloc of sorted) {
        if (total <= max + 0.01) break;
        const reduceBy = Math.min(alloc.amount, total - max);
        alloc.amount = Number((alloc.amount - reduceBy).toFixed(2));
        total -= reduceBy;
      }
    },

    smartAllocate() {
      const amount = Number(this.paymentAlloc.amount || 0);
      if (amount <= 0) return;
      this.paymentAlloc.allocations.forEach(a => a.amount = a.previouslyAllocated || 0);
      let remaining = amount;
      const sorted = [...this.paymentAlloc.allocations]
        .filter(a => a.remaining > 0.01)
        .sort((a, b) => a.date.localeCompare(b.date));
      for (const alloc of sorted) {
        if (remaining <= 0.01) break;
        const take = Math.min(remaining, alloc.remaining);
        const target = this.paymentAlloc.allocations.find(x => x.chargeId === alloc.chargeId);
        if (target) {
          target.amount = Number((target.amount + take).toFixed(2));
          remaining -= take;
        }
      }
    },

    removeAllocation(i) {
      this.paymentAlloc.allocations.splice(i, 1);
    },

    async openPaymentAllocation(paymentToEdit = null) {
      this.editingPaymentId = paymentToEdit ? paymentToEdit.id : null;
      const date = paymentToEdit ? paymentToEdit.datePaid : new Date().toISOString().substr(0, 10);
      const paymentAmount = paymentToEdit ? Math.abs(paymentToEdit.amountPaid) : null;
      const paymentReference = paymentToEdit?.reference || "";

      const todayStr = new Date().toISOString().split('T')[0];

      const charges = this.transactions
        .filter(t => Number(t.amountPaid) > 0)
        .map(t => ({
          chargeId: t.id,
          date: t.datePaid,
          description: t.description || t.chargeType || "Charge",
          totalAmount: t.amountPaid,
          allocatedAmount: t.allocatedAmount || 0,
          allocations: t.allocations || [],
          remaining: Number((t.amountPaid - (t.allocatedAmount || 0)).toFixed(2))
        }));

      const existingFromThisPayment = charges
        .flatMap(c => c.allocations
          .filter(alloc => alloc.paymentRef === paymentReference)
          .map(alloc => ({
            chargeId: c.chargeId,
            date: c.date,
            description: c.description,
            remaining: c.remaining,
            previouslyAllocated: alloc.amount,
            amount: alloc.amount
          }))
        );

      this.existingAllocations = [...existingFromThisPayment];

      let filteredCharges;
      if (this.editingPaymentId) {
        filteredCharges = charges.filter(c =>
          c.remaining > 0.01 ||
          c.allocations.some(alloc => alloc.paymentRef === paymentReference)
        );
      } else {
        filteredCharges = charges.filter(c => c.date <= todayStr);
      }

      const allocRows = filteredCharges.map(c => ({
        chargeId: c.chargeId,
        date: c.date,
        description: c.description,
        remaining: c.remaining,
        previouslyAllocated: c.allocations.find(alloc => alloc.paymentRef === paymentReference)?.amount || 0,
        amount: c.allocations.find(alloc => alloc.paymentRef === paymentReference)?.amount || 0
      }));

      allocRows.sort((a, b) => a.date.localeCompare(b.date));

      let autoRef = paymentReference;
      if (!this.editingPaymentId) {
        if (paymentAmount && date) {
          const cleanDate = date.replace(/-/g, '');
          const cleanAmount = Number(paymentAmount).toFixed(2);
          const ts = Math.floor(Date.now() / 1000);
          autoRef = `${this.leaseReference}.${cleanDate}.${cleanAmount}.${ts}`;
        } else {
          autoRef = "";
        }
      }

      this.paymentAlloc = {
        amount: paymentAmount,
        date: date,
        reference: autoRef,
        allocations: allocRows
      };

      this.showAddPaymentDialog = true;
    },

    cancelPaymentDialog() {
      this.showAddPaymentDialog = false;
      this.editingPaymentId = null;
      this.existingAllocations = [];
      this.paymentAlloc = { amount: null, date: "", reference: "", allocations: [] };
    },

    async savePaymentAllocation() {
      if (this.unallocatedAmount < -0.01) {
        alert("Over-allocated! Adjust amounts.");
        return;
      }
      this.savingPayment = true;
      const total = Number((this.paymentAlloc.amount || 0).toFixed(2));
      if (total <= 0 || !this.paymentAlloc.date) {
        alert("Invalid amount or date");
        this.savingPayment = false;
        return;
      }

      let paymentReference = this.paymentAlloc.reference;
      const looksUnique =
        typeof paymentReference === 'string' &&
        paymentReference.length > this.leaseReference.length + 4 &&
        paymentReference !== this.leaseReference &&
        paymentReference.includes('.');

      if (!looksUnique) {
        const cleanDate = this.paymentAlloc.date.replace(/-/g, '');
        const cleanAmount = Number(total).toFixed(2);
        const ts = Math.floor(Date.now() / 1000);
        paymentReference = `${this.leaseReference}.${cleanDate}.${cleanAmount}.${ts}`;
        this.paymentAlloc.reference = paymentReference;
      }

      const batch = writeBatch(db);
      let payRef;
      if (this.editingPaymentId) {
        payRef = doc(db, "Transactions", this.editingPaymentId);
        batch.update(payRef, {
          amountPaid: -total,
          datePaid: this.paymentAlloc.date,
          reference: paymentReference,
          type: "payment",
          leaseReference: this.leaseReference
        });
      } else {
        payRef = doc(collection(db, "Transactions"));
        batch.set(payRef, {
          leaseReference: this.leaseReference,
          datePaid: this.paymentAlloc.date,
          description: "Payment Received",
          reference: paymentReference,
          amountPaid: -total,
          type: "payment",
          allocatedAmount: 0,
          allocations: [],
          createdAt: new Date().toISOString()
        });
      }

      const newAllocMap = new Map();
      this.paymentAlloc.allocations.forEach(a => {
        const amt = Number(a.amount) || 0;
        if (a.chargeId && amt > 0.01) {
          newAllocMap.set(a.chargeId, Number(amt.toFixed(2)));
        }
      });

      for (const [chargeId, newAmount] of newAllocMap) {
        const chargeRef = doc(db, "Transactions", chargeId);
        const chargeSnap = await getDoc(chargeRef);
        if (!chargeSnap.exists()) continue;

        const chargeData = chargeSnap.data();
        let allocations = Array.isArray(chargeData.allocations) ? chargeData.allocations.slice() : [];

        const existingIndex = allocations.findIndex(alloc =>
          alloc.paymentRef === paymentReference ||
          alloc.paymentRef === this.paymentAlloc.reference
        );
        const oldAmount = existingIndex !== -1 ? Number(allocations[existingIndex].amount) || 0 : 0;

        if (newAmount > 0.01) {
          const allocEntry = {
            paymentRef: paymentReference,
            amount: Number(newAmount.toFixed(2)),
            date: this.paymentAlloc.date,
            paymentMethod: this.paymentAlloc.method || null,
            paymentId: payRef.id
          };
          if (existingIndex !== -1) {
            allocations[existingIndex] = allocEntry;
          } else {
            allocations.push(allocEntry);
          }
        } else if (oldAmount > 0.01) {
          allocations = allocations.filter(alloc => alloc.paymentRef !== paymentReference);
        }

        const newAllocatedAmount = Number(
          allocations.reduce((sum, alloc) => sum + (Number(alloc.amount) || 0), 0).toFixed(2)
        );

        batch.update(chargeRef, {
          allocations: allocations,
          allocatedAmount: newAllocatedAmount,
          type: 'charge',
          chargeType: chargeData.chargeType || chargeData.transactionType || 'Other Charge'
        });
      }

      for (const old of this.existingAllocations) {
        const newAmount = newAllocMap.get(old.chargeId) || 0;
        if (newAmount === 0 && old.previouslyAllocated > 0.01) {
          const chargeRef = doc(db, "Transactions", old.chargeId);
          const chargeSnap = await getDoc(chargeRef);
          if (chargeSnap.exists()) {
            const chargeData = chargeSnap.data();
            let allocations = Array.isArray(chargeData.allocations) ? chargeData.allocations : [];
            allocations = allocations.filter(
              alloc => alloc.paymentRef !== paymentReference &&
                       alloc.paymentRef !== this.paymentAlloc.reference
            );
            const newAllocatedAmount = Number(
              allocations.reduce((sum, alloc) => sum + (Number(alloc.amount) || 0), 0).toFixed(2)
            );
            batch.update(chargeRef, {
              allocations: allocations,
              allocatedAmount: newAllocatedAmount,
              type: 'charge'
            });
          }
        }
      }

      await batch.commit();
      alert(this.editingPaymentId ? "Payment updated successfully" : "Payment saved");
      this.cancelPaymentDialog();
      this.savingPayment = false;
      await this.fetchTransactions();
    },

    async submitAddCharge() {
      if (!this.addChargeForm.chargeType || !this.addChargeForm.amount || this.addChargeForm.amount <= 0 || !this.addChargeForm.date) {
        alert("Please fill in Charge Type, Amount, and Date");
        return;
      }
      const amount = Number(this.addChargeForm.amount);
      const chargeRef = doc(collection(db, "Transactions"));
      await setDoc(chargeRef, {
        leaseReference: this.leaseReference,
        datePaid: this.addChargeForm.date,
        description: this.addChargeForm.description || this.addChargeForm.chargeType,
        reference: this.addChargeForm.reference,
        amountPaid: amount,
        type: "charge",
        allocatedAmount: 0,
        allocations: [],
        chargeType: this.addChargeForm.chargeType,
        createdAt: new Date().toISOString()
      });
      this.addChargeForm = {
        chargeType: "Rent",
        amount: null,
        date: new Date().toISOString().substr(0, 10),
        description: "",
        reference: ""
      };
      this.showAddChargeDialog = false;
      await this.fetchTransactions();
    },

    openAddCreditDialog() {
      this.addCreditForm = {
        description: "",
        amount: null,
        date: new Date().toISOString().substr(0, 10),
        reference: ""
      };
      this.generateCreditReference();
      this.showAddCreditDialog = true;
    },

    generateCreditReference() {
      if (!this.addCreditForm.date || !this.addCreditForm.amount || this.addCreditForm.amount <= 0) {
        this.addCreditForm.reference = "";
        return;
      }
      const cleanDate = this.addCreditForm.date.replace(/-/g, '');
      const cleanAmount = Number(this.addCreditForm.amount).toFixed(2);
      const ts = Math.floor(Date.now() / 1000);
      this.addCreditForm.reference = `${this.leaseReference}.CREDIT.${cleanDate}.${cleanAmount}.${ts}`;
    },

    async submitAddCredit() {
      if (!this.addCreditForm.description || !this.addCreditForm.amount || this.addCreditForm.amount <= 0 || !this.addCreditForm.date) {
        alert("Please fill in Description, Amount, and Date");
        return;
      }
      const amount = Number(this.addCreditForm.amount);
      const creditRef = doc(collection(db, "Transactions"));
      await setDoc(creditRef, {
        leaseReference: this.leaseReference,
        datePaid: this.addCreditForm.date,
        description: this.addCreditForm.description,
        reference: this.addCreditForm.reference,
        amountPaid: -amount,
        type: "credit",
        createdAt: new Date().toISOString()
      });
      this.showAddCreditDialog = false;
      await this.fetchTransactions();
    },

    async addChargeInAllocation() {
      if (!this.newChargeInAllocation.chargeType || !this.newChargeInAllocation.amount || this.newChargeInAllocation.amount <= 0 || !this.newChargeInAllocation.date) {
        alert("Please fill in Charge Type, Amount, and Date");
        return;
      }
      const amount = Number(this.newChargeInAllocation.amount);
      const chargeRef = doc(collection(db, "Transactions"));
      await setDoc(chargeRef, {
        leaseReference: this.leaseReference,
        datePaid: this.newChargeInAllocation.date,
        description: this.newChargeInAllocation.description || this.newChargeInAllocation.chargeType,
        reference: "MANUAL CHARGE (from payment)",
        amountPaid: amount,
        type: "charge",
        allocatedAmount: 0,
        allocations: [],
        chargeType: this.newChargeInAllocation.chargeType,
        createdAt: new Date().toISOString()
      });
      this.paymentAlloc.allocations.push({
        chargeId: chargeRef.id,
        date: this.newChargeInAllocation.date,
        description: this.newChargeInAllocation.description || this.newChargeInAllocation.chargeType,
        remaining: amount,
        amount: 0,
        previouslyAllocated: 0
      });
      this.newChargeInAllocation = {
        chargeType: "Rent",
        amount: null,
        date: this.paymentAlloc.date || new Date().toISOString().substr(0, 10),
        description: ""
      };
      this.showAddChargeInAllocation = false;
      await this.fetchTransactions();
    },

    openDeleteDialog(item) {
      this.deleteItem = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.deleteItem?.id) return;
      this.deleting = true;
      await deleteDoc(doc(db, "Transactions", this.deleteItem.id));
      this.deleteDialog = false;
      this.deleting = false;
      this.deleteItem = null;
      await this.fetchTransactions();
    },

    editTransaction(item) {
      const tx = this.transactions.find(t => t.id === item.id);
      if (!tx) return;

      if (tx.type === 'payment' || (tx.amountPaid < 0 && tx.type === 'payment')) {
        this.openPaymentAllocation(tx);
      } else if (tx.type === 'credit') {
        this.editForm = {
          id: tx.id,
          amount: Math.abs(tx.amountPaid),
          datePaid: tx.datePaid,
          description: tx.description || "",
          reference: tx.reference || ""
        };
        this.showEditDialog = true;
      } else {
        this.editForm = {
          id: tx.id,
          amount: Math.abs(tx.amountPaid),
          datePaid: tx.datePaid,
          description: tx.description || "",
          reference: tx.reference || ""
        };
        this.showEditDialog = true;
      }
    },

    async submitEdit() {
      if (!this.editForm.id || !this.editForm.amount || this.editForm.amount <= 0 || !this.editForm.datePaid) {
        alert("Please fill in all required fields (Amount, Date)");
        return;
      }

      const originalTx = this.transactions.find(t => t.id === this.editForm.id);
      if (!originalTx) {
        alert("Transaction not found");
        return;
      }

      const newAmount = Number(this.editForm.amount);
      const oldAmount = Math.abs(originalTx.amountPaid);
      const amountChanged = Math.abs(newAmount - oldAmount) > 0.01;

      const txRef = doc(db, "Transactions", this.editForm.id);
      const updateData = {
        datePaid: this.editForm.datePaid,
        description: this.editForm.description || originalTx.description,
        reference: this.editForm.reference || originalTx.reference,
      };

      if (originalTx.amountPaid < 0) {
        updateData.amountPaid = -newAmount;
      } else {
        updateData.amountPaid = newAmount;
      }

      if (originalTx.type === 'charge' && amountChanged && newAmount < oldAmount) {
        updateData.allocations = [];
        updateData.allocatedAmount = 0;
        alert("Warning: Amount decreased. Allocations have been reset.");
      }

      try {
        await updateDoc(txRef, updateData);
        alert("Transaction updated successfully");
        this.showEditDialog = false;
        await this.fetchTransactions();
      } catch (error) {
        console.error("Error updating transaction:", error);
        alert("Failed to update: " + error.message);
      }
    },

    async fetchTransactions() {
      const q = query(collection(db, "Transactions"), where("leaseReference", "==", this.leaseReference), orderBy("datePaid"));
      const snap = await getDocs(q);
      this.transactions = snap.docs.map(d => {
        const data = d.data();
        if (typeof data.reference === 'string') {
          data.reference = data.reference.replace(/\.+$/, '');
        }
        if (!Array.isArray(data.allocations)) {
          data.allocations = [];
        }
        return { id: d.id, ...data };
      });
    },

    async fetchLeaseAndPropertyInfo() {
      const q = query(collection(db, "Leases"), where("leaseReference", "==", this.leaseReference));
      const snap = await getDocs(q);
      if (snap.empty) { this.tenantName = "Not Found"; this.propertyAddress = "Unknown"; return; }
      const data = snap.docs[0].data();
      this.tenantName = data.tenantName || "Unknown";
      const prop = await getDoc(doc(db, "properties", data.propertyId));
      this.propertyAddress = prop.exists() ? prop.data().displayName || "Unknown" : "Not Found";
    },

    async fetchLogoBase64() {
      const q = query(collection(db, 'Logo'), where('id', '==', 2));
      const snap = await getDocs(q);
      if (!snap.empty) this.base64log = snap.docs[0].data().logo2 || "";
    },

    async parseExcel() {
      if (!this.excelFile) return;
      this.parsingExcel = true;
      try {
        const buffer = await this.excelFile.arrayBuffer();
        const wb = XLSX.read(buffer, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        const fullText = rows.flat().join(' ');
        const fileLeaseRef = (fullText.match(/LEA\d{6,}/i) || [])[0]?.toUpperCase() || null;
        const amountDue = parseFloat((fullText.match(/AMOUNT DUE[:\s]*R?\s*([\d,]+\.?\d*)/i) || [])[1]?.replace(/,/g, '') || 0);
        const headerIdx = rows.findIndex(r => r.some(c => /DATE/i.test(String(c))) && r.some(c => /DESCRIPTION/i.test(String(c))));
        if (headerIdx === -1) throw new Error("Header not found");
        const header = rows[headerIdx];
        const dateCol = header.findIndex(c => /DATE/i.test(String(c)));
        const descCol = header.findIndex(c => /DESCRIPTION/i.test(String(c)));
        const debitCol = header.findIndex(c => /DEBIT/i.test(String(c)));
        const creditCol = header.findIndex(c => /CREDIT/i.test(String(c)));
        const transactions = [];
        let lastDate = null;
        const parseDate = v => {
          if (!v) return null;
          if (typeof v === 'number') {
            const d = new Date(Math.round((v - 25569) * 86400000));
            return d.toISOString().split('T')[0];
          }
          const m = String(v).match(/^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/);
          if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
          return null;
        };
        for (let i = headerIdx + 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.length < 2) continue;
          const rawDate = row[dateCol];
          let dateStr = parseDate(rawDate) || lastDate;
          if (!dateStr) continue;
          lastDate = dateStr;
          const desc = String(row[descCol] || '').trim();
          const debit = parseFloat(String(row[debitCol] || '0').replace(/,/g, '')) || 0;
          const credit = parseFloat(String(row[creditCol] || '0').replace(/,/g, '')) || 0;
          if (!desc && debit === 0 && credit === 0) continue;
          transactions.push({
            date: dateStr,
            description: desc.replace(/\[[^\]]*\]/g, '').trim(),
            debit: Number(debit.toFixed(2)),
            credit: Number(credit.toFixed(2))
          });
        }
        this.parsedStatement = {
          fileLeaseRef,
          amountDue,
          importTransactions: transactions.sort((a, b) => a.date.localeCompare(b.date))
        };
        this.importStep = 2;
        await this.detectRecurringCharges();
      } catch (e) {
        alert("Parse error: " + e.message);
      }
      this.parsingExcel = false;
    },

    async detectRecurringCharges() {
      this.detectedRecurring = [];
      if (!this.parsedStatement?.importTransactions?.length) return;
      const existingSnap = await getDocs(query(collection(db, "RecurringCharges"), where("leaseReference", "==", this.leaseReference)));
      const existingTypes = new Set(existingSnap.docs.map(d => d.data().chargeType));
      const seen = new Set();
      for (const t of this.parsedStatement.importTransactions) {
        if (t.debit <= 0.01) continue;
        const key = Object.keys(RECURRING_CHARGE_MAPPING).find(k => t.description.toLowerCase().includes(k.toLowerCase()));
        if (!key || seen.has(key)) continue;
        seen.add(key);
        const mapping = RECURRING_CHARGE_MAPPING[key];
        this.detectedRecurring.push({
          chargeType: mapping.chargeType,
          description: mapping.description,
          amount: t.debit,
          exists: existingTypes.has(mapping.chargeType),
          selected: true
        });
      }
      this.detectedRecurring.sort((a, b) => a.chargeType.localeCompare(b.chargeType));
    },

    async submitImportTransactions() {
      if (!confirm("This will DELETE all current transactions for this lease and replace them with the imported data. Are you sure?")) {
        return;
      }

      this.importing = true;

      const batch = writeBatch(db);

      try {
        const existingChargesSnap = await getDocs(
          query(collection(db, "Transactions"),
                where("leaseReference", "==", this.leaseReference),
                where("type", "==", "charge"))
        );
        const existingChargeMap = new Map();
        existingChargesSnap.docs.forEach(docSnap => {
          const d = docSnap.data();
          const key = `${d.datePaid}|${(d.description || '').trim().toLowerCase()}|${Number(d.amountPaid || 0).toFixed(2)}`;
          existingChargeMap.set(key, {
            id: docSnap.id,
            allocatedAmount: d.allocatedAmount || 0,
            allocations: d.allocations || []
          });
        });

        const existingPaymentsSnap = await getDocs(
          query(collection(db, "Transactions"),
                where("leaseReference", "==", this.leaseReference),
                where("type", "==", "payment"))
        );
        const existingPaymentMap = new Map();
        existingPaymentsSnap.docs.forEach(docSnap => {
          const d = docSnap.data();
          const key = `${d.datePaid}|${Number(Math.abs(d.amountPaid || 0)).toFixed(2)}`;
          existingPaymentMap.set(key, {
            reference: d.reference || '',
            allocations: d.allocations || []
          });
        });

        const oldTransactionsQuery = query(
          collection(db, "Transactions"),
          where("leaseReference", "==", this.leaseReference)
        );
        const oldSnap = await getDocs(oldTransactionsQuery);

        oldSnap.docs.forEach((docSnapshot) => {
          batch.delete(docSnapshot.ref);
        });

        const importTimestamp = Math.floor(Date.now() / 1000);
        let rowIndex = 0;

        for (const t of this.parsedStatement.importTransactions) {
          rowIndex++;

          const transactionDocRef = doc(collection(db, "Transactions"));

          const cleanDate = t.date.replace(/-/g, '');
          const absAmount = Math.abs(t.debit || t.credit || 0);
          const cleanAmount = absAmount.toFixed(2);

          const generatedReference = `${this.leaseReference}.${cleanDate}.${cleanAmount}.${importTimestamp}.${rowIndex.toString().padStart(3, '0')}`;

          const isPayment = t.credit > 0;
          let existing = null;
          if (isPayment) {
            existing = existingPaymentMap.get(`${t.date}|${absAmount.toFixed(2)}`);
          } else {
            const key = `${t.date}|${(t.description || '').trim().toLowerCase()}|${absAmount.toFixed(2)}`;
            existing = existingChargeMap.get(key);
          }

          batch.set(transactionDocRef, {
            leaseReference: this.leaseReference,
            datePaid: t.date,
            description: t.description,
            reference: isPayment
              ? (existing?.reference || generatedReference)
              : generatedReference,
            amountPaid: isPayment ? -t.credit : t.debit,
            type: isPayment ? "payment" : "charge",
            allocatedAmount: existing?.allocatedAmount || 0,
            allocations: existing?.allocations || [],
            createdBy: "excel-import",
            createdAt: new Date().toISOString(),
          });
        }

        for (const item of this.detectedRecurring.filter(i => i.selected)) {
          const recurringQuery = query(
            collection(db, "RecurringCharges"),
            where("leaseReference", "==", this.leaseReference),
            where("chargeType", "==", item.chargeType)
          );
          const recurringSnap = await getDocs(recurringQuery);

          if (recurringSnap.empty) {
            const newRecurringRef = doc(collection(db, "RecurringCharges"));
            batch.set(newRecurringRef, {
              leaseReference: this.leaseReference,
              chargeType: item.chargeType,
              transactionType: item.description,
              amount: item.amount,
              active: true,
              createdAt: new Date().toISOString(),
              lastImported: new Date().toISOString()
            });
          } else {
            const existingRef = recurringSnap.docs[0].ref;
            batch.update(existingRef, {
              amount: item.amount,
              lastImported: new Date().toISOString(),
            });
          }
        }

        await batch.commit();

        alert(`Import completed successfully!\n${this.parsedStatement.importTransactions.length} transactions imported.`);

      } catch (err) {
        console.error("Import failed:", err);
        alert("Import failed: " + (err.message || "Unknown error"));
      } finally {
        this.importing = false;
        this.cancelImport();
        await this.fetchTransactions();
      }
    },
    cancelImport() {
      this.showImportExcelDialog = false;
      this.importStep = 1;
      this.excelFile = null;
      this.parsedStatement = null;
      this.detectedRecurring = [];
    }
  },

  async created() {
    await Promise.all([
      this.fetchLeaseAndPropertyInfo(),
      this.fetchTransactions(),
      this.fetchLogoBase64()
    ]);
  }
};
</script>

<style scoped>
.error--text {
  color: #c62828 !important;
}

.success--text {
  color: #2e7d32 !important;
}

.gap-3 {
  gap: 12px;
}
</style>